import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AiOutlineDelete, AiOutlineEye } from "react-icons/ai";
import {
  getRecentlyViewed,
  getViewCounts,
  getCartCategories,
  rankByPopularity,
  trackCartCategory,
} from "../../utils/cookies";
import { API_BASE as API } from "../../api/api";
import { trackCheckoutStep, trackCartAbandon } from "../../utils/analytics";
import { hasConsent } from "../../utils/consent";
import { variantLabel } from "../product/ProductInfo";
import "./Cart.css";
import "../shared/AccountWallpaper.css";
import { imageProps, SIZES } from "../../utils/imageUrl";
import { useVatRate, vatPercentLabel } from "../../hooks/useVatRate";

const fmt      = (n) => `Ksh ${Number(n).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ── Mini recommendation card ────────────────────────────────────
const RecoCard = ({ product, onAddToCart, added }) => (
  <div className={`reco-card${added ? " reco-added" : ""}`}>
    {product.tag && <span className="reco-tag">{product.tag}</span>}
    <Link to={`/product/${product.slug ?? product.id}`} className="reco-img" aria-label={product.name}>
      {product.image_url && (
        <img {...imageProps(product.image_url, "product", SIZES.thumb)} alt={product.name} loading="lazy" decoding="async" />
      )}
    </Link>
    <div className="reco-info">
      <Link to={`/product/${product.slug ?? product.id}`} className="reco-name">{product.name}</Link>
      <p className="reco-cat">{product.category}</p>
      <div className="reco-footer">
        <span className="reco-price">{fmt(product.price)}</span>
        <button
          className={`reco-add${added ? " reco-in-cart" : ""}`}
          onClick={() => !added && onAddToCart(product)}
          disabled={added}
          title={added ? "Already in Selections" : "Add to Selections"}
        >
          {added ? "✓" : "+"}
        </button>
      </div>
    </div>
  </div>
);

// ── Card sizing is measured live from the DOM, never assumed from a
// constant — Cart.css resizes .reco-card at each breakpoint (and has a
// duplicate/conflicting @media rule), so a fixed width+gap pair can
// silently disagree with what's actually rendered. That mismatch is what
// let overflow:hidden slice through a card instead of cutting cleanly
// between cards.
const FALLBACK_CARD_W = 160;
const FALLBACK_GAP    = 16;

// Breakpoint cap on how many cards SHOULD show, keyed off the carousel's
// own measured width rather than window.innerWidth — the container is
// effectively full-viewport-width here, but this keeps the carousel
// correct even if it's ever placed somewhere narrower. Only the two
// mobile tiers are an intentional design cap (a wide phone/small tablet
// shouldn't cram in every card that would technically fit); above that,
// `fitCount` below already computes the real number of complete cards
// the measured container can hold, so there's nothing left to cap —
// hardcoding a desktop ceiling here just left empty space on wide
// screens instead of using it.
const desiredMaxForWidth = (w) => {
  if (w < 480) return 1;
  if (w < 760) return 2;
  return Infinity;
};

// ── Infinite-loop slideshow ─────────────────────────────────────
// Technique: clone [last N] at start + [first N] at end so the strip
// looks like: [clone-tail | real cards | clone-head].
// We start at offset N (pointing at real card 0). When we slide into
// the clone-head we silently snap (no transition) back to the real
// cards, giving the illusion of a seamless loop. Same for clone-tail.
const RecoSlideshow = ({ icon: Icon, title, products, cartIds, onAddToCart }) => {
  const [paused,  setPaused]  = useState(false);
  const [animate, setAnimate] = useState(true);
  // perView is derived from how many full cards actually fit in the
  // measured container, capped at the breakpoint's intended max and at
  // the item count — never assumed from window width alone.
  const [layout, setLayout] = useState({ perView: 1, cardW: FALLBACK_CARD_W, gap: FALLBACK_GAP });

  // outerRef measures AVAILABLE width — it is never itself resized by
  // this component, so measuring it can't feed back into its own size
  // (unlike measuring .reco-slideshow, which this component explicitly
  // sizes to `viewportWidth` below — observing that element would create
  // a shrink-on-every-frame feedback loop).
  const outerRef = useRef(null);
  const trackRef = useRef(null);

  const measure = useCallback(() => {
    const outer = outerRef.current;
    const track = trackRef.current;
    if (!outer || !track) return;
    const cards = track.querySelectorAll(".reco-card");
    if (cards.length === 0) return; // nothing rendered yet — a later call (resize, or the products-loaded effect) will measure

    const containerWidth = outer.clientWidth;
    const cardWidth = cards[0].getBoundingClientRect().width;
    if (!(cardWidth > 0)) return;

    let gap = FALLBACK_GAP;
    if (cards.length > 1) {
      gap = cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().right;
    } else {
      const parsed = parseFloat(window.getComputedStyle(track).columnGap || "0");
      if (!Number.isNaN(parsed)) gap = parsed;
    }

    const desiredMax = desiredMaxForWidth(containerWidth);
    const fitCount   = Math.max(1, Math.floor((containerWidth + gap) / (cardWidth + gap)));
    const perView    = Math.max(1, Math.min(desiredMax, fitCount, products.length || 1));

    setLayout((prev) => (
      prev.perView === perView && prev.cardW === cardWidth && prev.gap === gap
        ? prev
        : { perView, cardW: cardWidth, gap }
    ));
  }, [products.length]);

  // Re-measure whenever: the container resizes (window resize, layout
  // changes — Cart.css's media queries have already been applied by the
  // time ResizeObserver's callback fires), or the product list itself
  // changes (recommendations loading in asynchronously, or the cart
  // changing which items are excluded, changes card count/positions).
  useEffect(() => {
    measure();
    const outer = outerRef.current;
    if (!outer || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(outer);
    return () => ro.disconnect();
  }, [measure]);

  const { perView, cardW, gap } = layout;
  const N = perView;

  // Build the padded strip: [tail-clones] + [real] + [head-clones]
  const strip = useMemo(() => {
    if (products.length === 0) return [];
    const tail = products.slice(-N);   // last N items prepended
    const head = products.slice(0, N); // first N items appended
    return [...tail, ...products, ...head];
  }, [products, N]);

  // idx points into `strip`; start at N so we're on the first real card
  const [idx, setIdx] = useState(N);

  // Reset/clamp position whenever the strip rebuilds (product count or
  // the measured visible-card count changed)
  useEffect(() => {
    setAnimate(false);
    setIdx(N);
    // Re-enable animation after two frames so the DOM commits the position first
    requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
  }, [N, products.length]);

  const next = useCallback(() => setIdx((i) => i + 1), []);
  const prev = useCallback(() => setIdx((i) => i - 1), []);

  // After each animated slide, check whether we've entered a clone zone
  // and teleport back into the real cards without a visible transition.
  const handleTransitionEnd = useCallback(() => {
    const realLen = products.length;
    setIdx((i) => {
      if (i >= N + realLen) {
        // Slid into the head clones → jump to the real start
        setAnimate(false);
        requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
        return N;
      }
      if (i < N) {
        // Slid into the tail clones → jump to the real end
        setAnimate(false);
        requestAnimationFrame(() => requestAnimationFrame(() => setAnimate(true)));
        return N + realLen - 1;
      }
      return i;
    });
  }, [N, products.length]);

  // Auto-advance every 3.5 s; pause on hover
  useEffect(() => {
    if (paused || products.length <= perView) return;
    const iv = setInterval(next, 3500);
    return () => clearInterval(iv);
  }, [paused, products.length, perView, next]);

  if (!products.length) return null;

  const translateX    = -(idx * (cardW + gap));
  // Sized to exactly `perView` full cards — always <= the outer's
  // measured width by construction (fitCount above is floored), so
  // overflow:hidden on .reco-slideshow always cuts cleanly between
  // cards, never through one. No maxWidth fallback needed or wanted:
  // that would let a wide viewport silently show a fractional card.
  const viewportWidth = perView * cardW + (perView - 1) * gap;

  // Derive which real card is at position 0 of the viewport for the counter/dots
  const realIdx    = Math.min(Math.max(idx - N, 0), products.length - 1);
  const totalPages = Math.max(1, Math.ceil(products.length / perView));
  const currentPage = Math.floor(realIdx / perView);
  const canSlide   = products.length > perView;

  return (
    <section className="reco-section">
      <div className="reco-header">
        <div className="reco-title-group">
          <Icon className="reco-icon" />
          <h2 className="reco-title">{title}</h2>
        </div>

        {canSlide && (
          <div className="reco-slide-nav">
            <button className="reco-arrow" onClick={prev} aria-label="Previous">‹</button>
            <span className="reco-counter">{realIdx + 1} / {products.length}</span>
            <button className="reco-arrow" onClick={next} aria-label="Next">›</button>
          </div>
        )}
      </div>

      <div ref={outerRef} className="reco-viewport">
        <div
          className="reco-slideshow"
          style={{ width: `${viewportWidth}px` }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            ref={trackRef}
            className="reco-slide-track"
            style={{
              transform: `translateX(${translateX}px)`,
              transition: animate ? "transform 480ms cubic-bezier(0.4,0,0.2,1)" : "none",
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {strip.map((p, i) => (
              <RecoCard
                key={`${p.id}-${i}`}
                product={p}
                onAddToCart={onAddToCart}
                added={cartIds.has(p.id)}
              />
            ))}
          </div>
        </div>
      </div>

      {canSlide && totalPages > 1 && (
        <div className="reco-dots">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              className={`reco-dot${i === currentPage ? " reco-dot--active" : ""}`}
              onClick={() => {
                setAnimate(true);
                setIdx(N + i * perView);
              }}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

// ── Main Cart component ─────────────────────────────────────────
const Cart = ({ cart, onRemove, onClear, onAddToCart }) => {
  const navigate  = useNavigate();
  const [allProducts, setAllProducts] = useState([]);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const vatRate = useVatRate();   // 0 until Emporia is VAT-registered

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
  const vat      = subtotal * vatRate;
  const total    = subtotal + vat + deliveryFee;
  const totalWeight = cart.reduce((sum, item) => sum + (item.weight_kg ?? 0.5) * item.qty, 0);

  // Delivery fee depends on the order's total weight (the pickup station,
  // and thus distance, is chosen at checkout) — this is a baseline estimate.
  useEffect(() => {
    fetch(`${API}/pricing/delivery-fee?${new URLSearchParams({ weight_kg: totalWeight }).toString()}`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setDeliveryFee(d.delivery_fee))
      .catch(() => {});
  }, [totalWeight]);

  // Track which categories are in cart (cookie)
  useEffect(() => {
    cart.forEach((item) => trackCartCategory(item.category));
  }, [cart]);

  // Funnel: cart page viewed (with items)
  useEffect(() => {
    if (cart.length > 0) trackCheckoutStep("cart_viewed");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cart abandonment signal + (consent-gated) recovery snapshot on page exit
  useEffect(() => {
    const handleUnload = () => {
      if (cart.length === 0) return;
      const itemCount = cart.reduce((sum, i) => sum + i.qty, 0);
      const totalValue = cart.reduce((sum, i) => sum + i.price * i.qty, 0);
      trackCartAbandon(itemCount, totalValue);
      if (hasConsent("personalisation")) {
        try { localStorage.setItem("hr_cart_snapshot", JSON.stringify(cart)); } catch {}
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [cart]);

  // Fetch all products once for recommendations
  useEffect(() => {
    fetch(`${API}/products`)
      .then((r) => r.ok ? r.json() : [])
      .then(setAllProducts)
      .catch(() => {});
  }, []);

  // Derive recommendations (memo so they don't recalculate on every render)
  const { recentlyViewed, cartIds } = useMemo(() => {
    const cartIds = new Set(cart.map((i) => i.id));
    const rvIds = new Set(getRecentlyViewed());
    const rvProducts = allProducts.filter((p) => rvIds.has(p.id));

    const recentlyViewed = rankByPopularity(
      rvProducts, cartIds, getCartCategories(), getViewCounts()
    ).slice(0, 8);

    return { recentlyViewed, cartIds };
  }, [cart, allProducts]);

  // ── Empty cart ──────────────────────────────────────────────

  if (cart.length === 0) return (
    <main className="cart-empty account-wallpaper">
      <h2>Your selections are empty.</h2>
      <p>Discover our collections and add something exceptional.</p>
      <Link to="/" className="btn btn-gold">Continue Shopping</Link>
    </main>
  );

  // ── Cart with items ─────────────────────────────────────────

  return (
    <main className="cart-page account-wallpaper">
      <div className="cart-header">
        <h1>Your Selections</h1>
        <button className="cart-clear" onClick={onClear}>Clear all</button>
      </div>

      <div className="cart-layout">
        <div className="cart-items">
          {cart.map((item) => (
            <div key={item.id} className="cart-item">
              <div className="cart-item-image">
                {item.image_url && (
                  <img {...imageProps(item.image_url, "product", SIZES.thumb)} alt={item.name} loading="lazy" decoding="async" />
                )}
              </div>
              <div className="cart-item-info">
                <h4>{item.name}</h4>
                <p className="cart-item-category">{item.category}</p>
                {variantLabel(item) && <p className="cart-item-variant">{variantLabel(item)}</p>}
                <p className="cart-item-price">{fmt(item.price)} × {item.qty}</p>
                {item.is_preorder && (
                  <p className="cart-item-preorder-notice" role="status">
                    Pre-order — shipped from abroad, allow up to 2 weeks for delivery.
                  </p>
                )}
              </div>
              <button className="cart-remove" onClick={() => onRemove(item.id)} aria-label="Remove item">
                <AiOutlineDelete />
              </button>
            </div>
          ))}
        </div>

        <div className="cart-summary">
          <h3>Order Summary</h3>
          <div className="summary-row">
            <span>Subtotal</span>
            <span>{fmt(subtotal)}</span>
          </div>
          {vatRate > 0 && (
            <div className="summary-row">
              <span>VAT ({vatPercentLabel(vatRate)}%)</span>
              <span>{fmt(vat)}</span>
            </div>
          )}
          <div className="summary-row">
            <span>Delivery fee</span>
            <span>{fmt(deliveryFee)}</span>
          </div>
          <div className="summary-row total">
            <span>Total <span className="vat-note">({vatRate > 0 ? "VAT + delivery" : "delivery"} incl.)</span></span>
            <span>{fmt(total)}</span>
          </div>
          <button className="btn btn-gold full" onClick={() => navigate("/checkout")}>
            Proceed to Checkout
          </button>
          <Link to="/" className="cart-continue">← Continue Shopping</Link>
        </div>
      </div>

      {/* ── Recently Viewed slideshow ── */}
      {recentlyViewed.length > 0 && (
        <div className="reco-area">
          <RecoSlideshow
            icon={AiOutlineEye}
            title="Recently Viewed"
            products={recentlyViewed}
            cartIds={cartIds}
            onAddToCart={onAddToCart}
          />
        </div>
      )}
    </main>
  );
};

export default Cart;
